import apiClient from "./apiClient";
import { toId } from "../utils/ids";

const BASE = "/api/Appointment";

const toDateTime = (d) => (d ? `${d}T00:00:00` : null);

const toTimeSpan = (t) => {
  if (!t) return null;
  const clean = t.split(".")[0]; // strip microseconds
  return clean.length === 5 ? `${clean}:00` : clean; // ensure "HH:mm:ss"
};

// One calendar slot. The clinic books in 10-minute slots, so a booking without a toTime is
// one slot long — not the procedure's default duration, which is 30 for most procedures.
export const DEFAULT_DURATION = 10;

// If toTime is missing, default to fromTime + one slot
const toTimeOrDefault = (toTime, fromTime) => {
  if (toTime) return toTimeSpan(toTime);
  if (!fromTime) return null;
  const [h, m] = fromTime.split(":").map(Number);
  const total = (h || 0) * 60 + (m || 0) + DEFAULT_DURATION;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}:00`;
};

// Get appointments by date (YYYY-MM-DD)
export const getAppointments = (params) => {
  const date = params?.date || params?.from || new Date().toISOString().split("T")[0];
  return apiClient.get(`${BASE}/date/${date}`);
};

// Get appointments for a specific doctor on a specific date
export const getAppointmentsByDoctor = (doctorId, date) =>
  apiClient.get(`${BASE}/doctor/${doctorId}/date/${date}`);

// For the calendar: fetch by date (single day at a time)
export const getCalendarAppointments = (from) => {
  // toISOString() would convert "now" to UTC, shifting the date backward a day in
  // positive-UTC-offset timezones (e.g. IST) — use local date parts instead.
  const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const date = from || today();
  return apiClient.get(`${BASE}/date/${date}`);
};

export const getAppointment = (id) => apiClient.get(`${BASE}/${id}`);

// Always send a valid toTime (default: fromTime + one slot)
const toPayload = (data) => ({
  appointmentDate: toDateTime(data.appointmentDate),
  fromTime: toTimeSpan(data.fromTime),
  toTime: toTimeOrDefault(data.toTime, data.fromTime),
  patientID: toId(data.patientId || data.patientID),
  doctorID: toId(data.doctorId || data.doctorID),
  procedureID: toId(data.procedureId || data.procedureID),
  status: data.status || "Booked",
  remarks: data.remarks || "",
});

export const createAppointment = (data) => apiClient.post(`${BASE}/book`, toPayload(data));

// Edit a booked appointment's details (patient, doctor, procedure, date, times, remarks).
// Requested in BACKEND_CHANGES_ROUND5.md — same body as /book, plus the appointmentID.
export const updateAppointmentDetails = (id, data) =>
  apiClient.put(`${BASE}/${id}`, { ...toPayload(data), appointmentID: id });

// Status-only update
export const updateAppointment = (id, data) =>
  apiClient.post(`${BASE}/update-status`, {
    appointmentID: id,
    status: data.status,
  });

export const deleteAppointment = () => Promise.reject(new Error("Delete not supported by API"));

export const getNextToken = () => Promise.resolve({ data: { token: "" } });
